import React, { useEffect, useState } from "react";
import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";
import { WorkOrderApi } from "../../../api/workorders/service";

// PDF Styles (mirrors the approach used in ContractorPaymentModal)
const pdfStyles = StyleSheet.create({
  page: {
    padding: 24,
    fontFamily: "Helvetica",
    fontSize: 10,
    color: "#232323",
  },
  header: {
    marginBottom: 16,
    borderBottom: "2 solid #3B0069",
    paddingBottom: 10,
  },
  title: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#3B0069",
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 10,
    color: "#666",
  },
  section: {
    marginBottom: 14,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#232323",
    marginBottom: 6,
    backgroundColor: "#F5F5F5",
    padding: 6,
  },
  row: {
    flexDirection: "row",
    marginBottom: 6,
    paddingHorizontal: 6,
  },
  label: {
    width: "40%",
    fontSize: 10,
    color: "#737373",
  },
  value: {
    width: "60%",
    fontSize: 10,
    fontWeight: "bold",
    color: "#232323",
  },
  table: {
    width: "100%",
    border: "1 solid #E5E7EB",
    borderRadius: 2,
    marginTop: 2,
  },
  tableRow: {
    flexDirection: "row",
  },
  tableHeaderCell: {
    flexGrow: 1,
    flexBasis: 0,
    backgroundColor: "#F5F5F5",
    padding: 6,
    fontSize: 10,
    fontWeight: "bold",
    borderRight: "1 solid #E5E7EB",
  },
  tableCell: {
    flexGrow: 1,
    flexBasis: 0,
    padding: 6,
    fontSize: 10,
    borderRight: "1 solid #E5E7EB",
  },
  footer: {
    position: "absolute",
    bottom: 24,
    left: 24,
    right: 24,
    borderTop: "1 solid #CCCCCC",
    paddingTop: 8,
    fontSize: 8,
    color: "#999",
    textAlign: "center",
  },
});

const pad3 = (n) => String(n).padStart(3, "0");

const formatLocalDateTime = (d) => {
  if (!d) return "—";
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return "—";
  return `${date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })} ${date.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

// PDF Document Component
export const WorkOrderInvoiceDocument = ({ contractor, workOrder }) => {
  const id = workOrder?.id ?? "-";
  const site = workOrder?.site ?? "-";
  const issueDate = workOrder?.date ?? "";
  const value = Number(workOrder?.value ?? 0);
  const paid = Number(workOrder?.paid ?? 0);
  const remaining = Math.max(0, value - paid);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Read organization details from localStorage
  const getLocalValue = (key) => {
    try {
      const val =
        typeof window !== "undefined" ? localStorage.getItem(key) : null;
      if (!val || val === "null" || val === "undefined") return null;
      return val;
    } catch {
      return null;
    }
  };
  const orgName = JSON.parse(getLocalValue("organization"))?.organization_name;
  const businessAddress = JSON.parse(getLocalValue("organization"))?.business_address;
  const contactNumber = JSON.parse(getLocalValue("organization"))?.contact_number;

  // Try to normalize optional work items if present on workOrder
  const workItems = Array.isArray(workOrder?.items || workOrder?.work_items)
    ? (workOrder.items || workOrder.work_items).map((it) => ({
        quantity:
          it?.quantity ??
          it?.qty ??
          it?.count ??
          (it ? "x1" : "-"),
        description:
          it?.description ??
          it?.name ??
          it?.item ??
          it?.title ??
          "-",
        code: it?.code ?? it?.work_code ?? it?.sku ?? "-",
        price:
          it?.price ??
          it?.rate ??
          it?.unit_price ??
          it?.amount ??
          null,
      }))
    : [];

  useEffect(() => {
    let active = true;
    async function load() {
      if (!workOrder?.id) return;
      setHistoryLoading(true);
      try {
        const data = await WorkOrderApi.getContractHistory(workOrder.id);
        const rows = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
          ? data.data
          : data?.results || [];
        if (active) {
          setHistory(rows);
        }
      } catch (e) {
        if (active) {
          setHistory([]);
        }
      } finally {
        if (active) setHistoryLoading(false);
      }
    }
    load();
    return () => {
      active = false;
    };
  }, [workOrder?.id]);

  return (
    <Document>
      {/* Create a taller single page to avoid automatic pagination */}
      <Page size={{ width: 595.28, height: 1400 }} style={pdfStyles.page}>
        {/* Header */}
        <View style={pdfStyles.header}>
          <Text style={pdfStyles.title}>Contractor Work Order Invoice</Text>
          <Text style={pdfStyles.subtitle}>
            Invoice ID: WO_{pad3(id)}
          </Text>
        </View>


        {/* Work Order From */}
        <View style={pdfStyles.section}>
          <Text style={pdfStyles.sectionTitle}>Work Order From</Text>
          <View style={pdfStyles.table}>
            <View
              style={[
                pdfStyles.tableRow,
                { borderBottom: "1 solid #E5E7EB" },
              ]}
            >
              <Text style={pdfStyles.tableHeaderCell}>Name of Company</Text>
              <Text style={pdfStyles.tableHeaderCell}>Address</Text>
              <Text style={[pdfStyles.tableHeaderCell, { borderRight: "0" }]}>
                Contact
              </Text>
            </View>
            <View style={pdfStyles.tableRow}>
              <Text style={pdfStyles.tableCell}>{orgName || "N/A"}</Text>
              <Text style={pdfStyles.tableCell}>{businessAddress || "N/A"}</Text>
              <Text style={[pdfStyles.tableCell, { borderRight: "0" }]}>
                {contactNumber || "N/A"}
              </Text>
            </View>
          </View>
        </View>

        {/* Contractor Information */}
        <View style={pdfStyles.section}>
          <Text style={pdfStyles.sectionTitle}>Contractor Details</Text>
          <View style={pdfStyles.table}>
            <View
              style={[
                pdfStyles.tableRow,
                { borderBottom: "1 solid #E5E7EB" },
              ]}
            >
              <Text style={pdfStyles.tableHeaderCell}>Contractor Name</Text>
              <Text style={pdfStyles.tableHeaderCell}>Address</Text>
              <Text
                style={[pdfStyles.tableHeaderCell, { borderRight: "0" }]}
              >
                Contact Details
              </Text>
            </View>
            <View style={pdfStyles.tableRow}>
              <Text style={pdfStyles.tableCell}>
                {contractor?.name || "-"}
              </Text>
              <Text style={pdfStyles.tableCell}>
                {contractor?.address || "N/A"}
              </Text>
              <Text style={[pdfStyles.tableCell, { borderRight: "0" }]}>
                {contractor?.contact || "N/A"}
              </Text>
            </View>
          </View>
        </View>

        {/* Work Order Details */}
        <View style={pdfStyles.section}>
          <Text style={pdfStyles.sectionTitle}>Work Order Details</Text>
          <View style={pdfStyles.table}>
            <View
              style={[
                pdfStyles.tableRow,
                { borderBottom: "1 solid #E5E7EB" },
              ]}
            >
              <Text style={pdfStyles.tableHeaderCell}>Work Order ID</Text>
              <Text style={pdfStyles.tableHeaderCell}>Site/Project</Text>
              <Text style={pdfStyles.tableHeaderCell}>Date of Issue</Text>
              <Text style={pdfStyles.tableHeaderCell}>Amount Paid</Text>
              <Text style={[pdfStyles.tableHeaderCell, { borderRight: "0" }]}>
                Remaining
              </Text>
            </View>
            <View style={pdfStyles.tableRow}>
              <Text style={pdfStyles.tableCell}>WO_{pad3(id)}</Text>
              <Text style={pdfStyles.tableCell}>{site}</Text>
              <Text style={pdfStyles.tableCell}>{issueDate}</Text>
              <Text style={pdfStyles.tableCell}>
                ₹{paid.toLocaleString("en-IN")}
              </Text>
              <Text style={[pdfStyles.tableCell, { borderRight: "0" }]}>
                ₹{remaining.toLocaleString("en-IN")}
              </Text>
            </View>
          </View>
        </View>

        {/* Work Order Value */}
        <View style={pdfStyles.section}>
          <Text style={pdfStyles.sectionTitle}>Work Order Value</Text>
          <View style={pdfStyles.table}>
            <View style={[pdfStyles.tableRow, { borderBottom: "1 solid #E5E7EB" }]}>
              <Text style={[pdfStyles.tableHeaderCell, { borderRight: "0" }]}>
                Total Value
              </Text>
            </View>
            <View style={pdfStyles.tableRow}>
              <Text style={[pdfStyles.tableCell, { borderRight: "0" }]}>
                ₹{value.toLocaleString("en-IN")}
              </Text>
            </View>
          </View>
        </View>

        {/* Contract History */}
        <View style={pdfStyles.section}>
          <Text style={pdfStyles.sectionTitle}>Contract History</Text>
          {historyLoading ? (
            <View style={pdfStyles.row}>
              <Text style={pdfStyles.value}>Loading...</Text>
            </View>
          ) : history.length === 0 ? (
            <View style={pdfStyles.row}>
              <Text style={pdfStyles.value}>No history available</Text>
            </View>
          ) : (
            (history || []).slice(0, 3).map((contract, idx) => (
              <View
                key={idx}
                style={{
                  border: "1 solid #E5E7EB",
                  borderRadius: 4,
                  marginBottom: 6,
                  padding: 6,
                }}
              >
                <View style={pdfStyles.row}>
                  <Text style={pdfStyles.label}>End Date:</Text>
                  <Text style={pdfStyles.value}>
                    {contract?.end_date || "—"}
                  </Text>
                </View>
                <View style={pdfStyles.row}>
                  <Text style={pdfStyles.label}>Updated:</Text>
                  <Text style={pdfStyles.value}>
                    {formatLocalDateTime(contract?.updated_on)}
                  </Text>
                </View>
                <View style={pdfStyles.row}>
                  <Text style={pdfStyles.label}>Contract Amount:</Text>
                  <Text style={pdfStyles.value}>
                    ₹{Number(contract?.contract_amount || 0).toLocaleString("en-IN")}
                  </Text>
                </View>
                <View style={pdfStyles.row}>
                  <Text style={pdfStyles.label}>Scope of Work:</Text>
                  <Text style={pdfStyles.value}>
                    {contract?.scope_of_work || "—"}
                  </Text>
                </View>
                <View style={pdfStyles.row}>
                  <Text style={pdfStyles.label}>Payment Terms:</Text>
                  <Text style={pdfStyles.value}>
                    {contract?.payment_terms || "—"}
                  </Text>
                </View>
                <View style={pdfStyles.row}>
                  <Text style={pdfStyles.label}>Terms & Conditions:</Text>
                  <Text style={pdfStyles.value}>
                    {contract?.terms_and_conditions || "—"}
                  </Text>
                </View>
                <View style={pdfStyles.row}>
                  <Text style={pdfStyles.label}>Approval & Acknowledgement:</Text>
                  <Text style={pdfStyles.value}>
                    {contract?.approval_and_acknowledgement || "—"}
                  </Text>
                </View>
              </View>
            ))
          )}
          {/* If there are more items, indicate truncation */}
          {history.length > 3 && (
            <View style={{ paddingHorizontal: 6, marginTop: 2 }}>
              <Text style={{ fontSize: 9, color: "#737373" }}>
                Showing 3 of {history.length} entries
              </Text>
            </View>
          )}
        </View>

        {/* Footer */}
        <View style={pdfStyles.footer}>
          <Text>This is a computer-generated invoice and does not require a signature.</Text>
          <Text>Generated on {new Date().toLocaleString("en-IN")}</Text>
        </View>
      </Page>
    </Document>
  );
};


